import { useMemo } from 'react';

import type { TaskActions } from '@/components/tasks/TaskList';
import {
  useCompleteTask,
  useDeleteTask,
  useMoveTask,
  useReopenTask,
  useSetPriority,
  useSetStage,
} from '@/hooks/useTasks';
import type { TaskSummary } from '@/lib/api/types';
import { clearPending, isPending, markPending } from '@/lib/pendingCompletion';

/**
 * The action set every task list needs, wired to the optimistic mutations.
 *
 * Built once here rather than in each screen so that ticking a box behaves
 * identically on Today, Backlog and Assigned to me — including the rollback.
 *
 * Completion is **two-stage**: the first tick only strikes the row through in
 * place (client-side, nothing sent), and the row then offers "return" and
 * "finish". Only confirming completes the task on the server. A tick that is
 * never confirmed leaves the task open — the safe direction to fail.
 */
export function useTaskActions(openTask: (taskId: string) => void): TaskActions {
  const complete = useCompleteTask();
  const reopen = useReopenTask();
  const move = useMoveTask();
  const setPriority = useSetPriority();
  const setStage = useSetStage();
  const remove = useDeleteTask();

  return useMemo(
    () => ({
      onOpen: openTask,
      onToggleComplete: (task: TaskSummary) => {
        // A completed row's checkbox reopens it — the way back stays one click.
        if (task.status === 'COMPLETED') reopen.mutate(task.id);
        // Ticking a pending row again is the "no, I did not mean it" path.
        else if (isPending(task.id)) clearPending(task.id);
        else markPending(task.id);
      },
      onConfirmComplete: (task: TaskSummary) => {
        clearPending(task.id);
        complete.mutate(task.id);
      },
      onCompleteStep: (task: TaskSummary) => {
        if (task.status === 'COMPLETED') reopen.mutate(task.id);
        else complete.mutate(task.id);
      },
      onMove: (taskId, target, date) =>
        move.mutate({ taskId, target, ...(date ? { date } : {}) }),
      onSetPriority: (taskId, priority) => setPriority.mutate({ taskId, priority }),
      onSetStage: (taskId, status) => setStage.mutate({ taskId, status }),
      onDelete: (taskId) => remove.mutate(taskId),
    }),
    [openTask, complete, reopen, move, setPriority, setStage, remove],
  );
}
